import { DatamapDoc, DatamapRow } from "types/datamap.types"

export type NewDatamapRow = Omit<DatamapRow, "id">

export interface BulkCodeChange {
  doc: DatamapDoc
  /** Labels of the rows whose code changed. */
  changed: string[]
  /** Labels of the rows that already had the code. */
  unchanged: string[]
  /** Rows left alone because the code already belongs to another answer there. */
  skipped: { label: string; usedBy: string }[]
}

/** Codes compare as numbers when both are whole numbers ("007" is 7), otherwise as text. */
export const sameCode = (a: string, b: string): boolean =>
  /^\d+$/.test(a) && /^\d+$/.test(b) ? Number(a) === Number(b) : a === b

const nextIdNumber = (doc: DatamapDoc): number =>
  doc.rows.reduce((max, row) => Math.max(max, Number(row.id.slice(1)) || 0), 0) + 1

const mapRow = (doc: DatamapDoc, rowId: string, change: (row: DatamapRow) => DatamapRow): DatamapDoc => ({
  ...doc,
  rows: doc.rows.map((row) => (row.id === rowId ? change(row) : row)),
})

export const updateRow = (doc: DatamapDoc, rowId: string, patch: Partial<NewDatamapRow>): DatamapDoc =>
  mapRow(doc, rowId, (row) => ({ ...row, ...patch }))

export const removeRow = (doc: DatamapDoc, rowId: string): DatamapDoc => ({
  ...doc,
  rows: doc.rows.filter((row) => row.id !== rowId),
})

export const addRows = (doc: DatamapDoc, rows: NewDatamapRow[]): DatamapDoc => {
  const first = nextIdNumber(doc)
  return { ...doc, rows: [...doc.rows, ...rows.map((row, i) => ({ ...row, id: `r${first + i}` }))] }
}

/** A blank TEXT row, first in the list so it shows on the grid's first page. */
export const addBlankRow = (doc: DatamapDoc): DatamapDoc => ({
  ...doc,
  rows: [{ id: `r${nextIdNumber(doc)}`, label: "", type: "TEXT", variable: "", fieldMetadata: "", values: [] }, ...doc.rows],
})

/** Points every row that reads `from` at the data column `to` (both rows of a coded twin). */
export const renameVariable = (doc: DatamapDoc, from: string, to: string): DatamapDoc => ({
  ...doc,
  rows: doc.rows.map((row) => (row.variable === from ? { ...row, variable: to } : row)),
})

export const removeRowsWithVariable = (doc: DatamapDoc, variable: string): DatamapDoc => ({
  ...doc,
  rows: doc.rows.filter((row) => row.variable !== variable),
})

export const addValue = (doc: DatamapDoc, rowId: string, code: string, label: string): DatamapDoc =>
  mapRow(doc, rowId, (row) => ({ ...row, values: [...row.values, { code, label }] }))

/** Gives the answer coded `fromCode` the code `toCode`, as when the scripter's template used 996 for Don't know. */
export const recodeValue = (doc: DatamapDoc, rowId: string, fromCode: string, toCode: string): DatamapDoc =>
  mapRow(doc, rowId, (row) => ({
    ...row,
    values: row.values.map((value) => (sameCode(value.code, fromCode) ? { ...value, code: toCode } : value)),
  }))

/**
 * Sets the code of the answer labelled `valueLabel` in every category row that has it. A row
 * where `code` already belongs to another answer is left alone and listed in `skipped`.
 */
export const setCodeForLabel = (doc: DatamapDoc, valueLabel: string, code: string): BulkCodeChange => {
  const changed: string[] = []
  const unchanged: string[] = []
  const skipped: { label: string; usedBy: string }[] = []

  const rows = doc.rows.map((row) => {
    if (row.type !== "CAT") return row
    const target = row.values.find((value) => value.label === valueLabel)
    if (!target) return row
    if (sameCode(target.code, code)) {
      unchanged.push(row.label)
      return row
    }
    const clash = row.values.find((value) => value !== target && sameCode(value.code, code))
    if (clash) {
      skipped.push({ label: row.label, usedBy: clash.label })
      return row
    }
    changed.push(row.label)
    return { ...row, values: row.values.map((value) => (value === target ? { ...value, code } : value)) }
  })

  return { doc: { ...doc, rows }, changed, unchanged, skipped }
}

/** Answer labels in category rows, most used first, with the codes they have now. */
export const categoryLabelsInUse = (doc: DatamapDoc): { label: string; rows: number; codes: string[] }[] => {
  const byLabel = new Map<string, { rows: number; codes: Set<string> }>()
  for (const row of doc.rows) {
    if (row.type !== "CAT") continue
    for (const value of row.values) {
      if (value.label === "") continue
      const entry = byLabel.get(value.label) ?? { rows: 0, codes: new Set<string>() }
      entry.rows += 1
      entry.codes.add(value.code)
      byLabel.set(value.label, entry)
    }
  }
  return [...byLabel.entries()]
    .map(([label, entry]) => ({ label, rows: entry.rows, codes: [...entry.codes].sort((a, b) => Number(a) - Number(b)) }))
    .sort((a, b) => b.rows - a.rows || a.label.localeCompare(b.label))
}
